import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { FormikErrors, useFormik } from 'formik'
import React from 'react'
import { Control, House } from '../../types'
import { pinValidate } from '../../utils/businessUtil'
import { PinIndicator, getUsedPins } from '../ControlForm/PinIndicator'

type TwayFormFields = {
	label: string
	activePin: number | null // 三方弁開けるピン
	negativePin: number | null // 三方弁閉めるピン
	relatedHpId: number | null // 関連ヒートポンプ
	timer: number // 固定実行時間 (60秒)
}

type Props = {
	disabled: boolean
	handleSubmit: (fields: TwayFormFields) => void
	availableHps: Array<{ id: number; label: string }> // 利用可能なHP機器のリスト
	house?: House
	existingControls?: Control[]
}

function TwayForm(props: Props) {
	const {
		values,
		errors,
		touched,
		handleChange,
		handleSubmit,
		setFieldTouched,
		setFieldValue,
	} = useFormik<TwayFormFields>({
		initialValues: {
			label: '三方弁',
			activePin: null,
			negativePin: null,
			relatedHpId: null,
			timer: 0,
		},
		validate: (values) => {
			const errors: FormikErrors<TwayFormFields> = {}

			if (!values.label) {
				errors.label = '必須です。'
			}

			if (pinValidate(values.activePin)) {
				errors.activePin = '1から16の値にしてください。'
			}
			if (pinValidate(values.negativePin)) {
				errors.negativePin = '1から16の値にしてください。'
			}

			if (!values.activePin && !values.negativePin) {
				errors.activePin =
					'開けるピンか閉めるピンを少なくとも1つ設定してください。'
			}

			return errors
		},
		onSubmit: (fields: TwayFormFields) => {
			props.handleSubmit(fields)
		},
	})

	const change = (
		name: keyof TwayFormFields,
		e: React.ChangeEvent<unknown>,
	) => {
		e.persist()
		handleChange(e)
		setFieldTouched(name, true, false)
	}

	const { usedPins, twayPins } = getUsedPins(props.existingControls || [])

	return (
		<div>
			<Typography>三方弁機器を作成</Typography>
			<form style={{ display: 'grid', gap: '8px' }}>
				<TextField
					name="label"
					type="text"
					autoComplete="pf-label"
					error={touched.label && Boolean(errors.label)}
					required
					disabled={props.disabled}
					label="名前"
					helperText={touched.label && errors.label}
					value={values.label}
					inputProps={{ 'data-test': 'tway-input-label' }}
					onChange={change.bind(null, 'label')}
					fullWidth
				/>
				<TextField
					name="activePin"
					type="number"
					autoComplete="pf-activePin"
					error={touched.activePin && Boolean(errors.activePin)}
					required
					disabled={props.disabled}
					label="三方弁開けるピン(1~16)"
					helperText={touched.activePin && errors.activePin}
					value={values.activePin}
					inputProps={{ 'data-test': 'tway-input-activePin' }}
					onChange={change.bind(null, 'activePin')}
					fullWidth
				/>
				<TextField
					name="negativePin"
					type="number"
					autoComplete="pf-negativePin"
					error={touched.negativePin && Boolean(errors.negativePin)}
					required
					disabled={props.disabled}
					label="三方弁閉めるピン(1~16)"
					helperText={touched.negativePin && errors.negativePin}
					value={values.negativePin}
					inputProps={{ 'data-test': 'tway-input-negativePin' }}
					onChange={change.bind(null, 'negativePin')}
					fullWidth
				/>
				<PinIndicator
					usedPins={usedPins}
					twayPins={twayPins}
					house={props.house}
					selectedPins={
						new Set(
							[values.activePin, values.negativePin].filter(
								(p): p is number => p != null,
							),
						)
					}
					onPinClick={(pin) => {
						// 開けるピン→閉めるピンの順に埋める（両方埋まっていたら開けるピンを更新）
						if (values.activePin === pin || values.negativePin === pin) {
							if (values.activePin === pin) setFieldValue('activePin', null)
							else setFieldValue('negativePin', null)
							return
						}
						if (values.activePin == null) setFieldValue('activePin', pin)
						else if (values.negativePin == null)
							setFieldValue('negativePin', pin)
						else setFieldValue('activePin', pin)
					}}
				/>
				<TextField
					select
					name="relatedHpId"
					autoComplete="pf-relatedHpId"
					error={touched.relatedHpId && Boolean(errors.relatedHpId)}
					disabled={props.disabled}
					label="関連ヒートポンプ（任意）"
					helperText={
						touched.relatedHpId && errors.relatedHpId
							? errors.relatedHpId
							: '関連するHP機器を選択できます'
					}
					value={values.relatedHpId ?? ''}
					inputProps={{ 'data-test': 'tway-input-relatedHpId' }}
					onChange={(e) => {
						const value = e.target.value === '' ? null : Number(e.target.value)
						setFieldValue('relatedHpId', value)
						setFieldTouched('relatedHpId', true, false)
					}}
					fullWidth
				>
					<MenuItem value="">なし</MenuItem>
					{props.availableHps.map((hp) => (
						<MenuItem key={hp.id} value={hp.id}>
							{hp.label}
						</MenuItem>
					))}
				</TextField>
				<Button
					variant="contained"
					color="primary"
					onClick={() => handleSubmit()}
					disabled={props.disabled}
					size={'large'}
				>
					追加する
				</Button>
			</form>
		</div>
	)
}

export default TwayForm
